'use client';

import type { ReactNode } from 'react';
import type { GlobalFilter, LocationData, RoomInfo } from '@/app/(pages)/results/type';
import type { SORT_TYPE, UniversalSearchHeaderResponse } from '@weberus/shared-types';
import { useHydrateAtoms } from 'jotai/utils';
import { hashedMemberNoAtom, isLocationAgreedAtom } from '@/app/_shared/store/member';
import {
  globalFilterAtom,
  globalFilterParamsAtom,
  originalGlobalFilterAtom,
  quickFilterAtom,
  searchFilterKeywordAtom,
  searchFilterSortAtom,
  sessionKeyAtom,
  sortInfoAtom,
  searchTypeKeyAtom,
} from '@/app/_shared/store/search-filter';
import { localLocationAtom } from '@/app/_shared/store/location';
import {
  selectedVerticalTabInfoAtom,
  universalSearchRequestBodyAtom,
  verticalTabInfoAtom,
} from '@/app/_shared/store/universal-search';
import {
  capacityAdultAtom,
  capacityChildAgesAtom,
  capacityChildAtom,
  checkInDateAtom,
  checkOutDateAtom,
} from '@/app/_shared/store/local-accommodation';
import { globalCheckIOCapacityAtom } from '@/app/_shared/store/global-accommodation';

interface UniversalSearchProviderProps {
  children: ReactNode;
  searchHeader: UniversalSearchHeaderResponse;
  checkInDate: string;
  checkOutDate: string;
  capacityAdult: number;
  capacityChild: number;
  capacityChildAges: number[];
  globalCheckInDate: string;
  globalCheckOutDate: string;
  globalCapacityRooms: RoomInfo[];
  keyword?: string;
  sort: keyof typeof SORT_TYPE;
  searchTypeKey: 'category';
  filters: GlobalFilter[] | undefined;
  localLocation: Partial<LocationData>;
  sessionKey: string;
  hashedMemberNo: string | undefined;
  isLocationAgreed: boolean;
}

export function UniversalSearchProvider({
  children,
  searchHeader,
  checkInDate,
  checkOutDate,
  capacityAdult,
  capacityChild,
  capacityChildAges,
  globalCheckInDate,
  globalCheckOutDate,
  globalCapacityRooms,
  keyword,
  sort,
  searchTypeKey,
  filters,
  localLocation,
  sessionKey,
  hashedMemberNo,
  isLocationAgreed,
}: UniversalSearchProviderProps) {
  useHydrateAtoms([
    [searchFilterKeywordAtom, keyword],
    [
      searchFilterSortAtom,
      sort || searchHeader?.sortInfo?.items?.filter((item) => item.isDefault)[0]?.key || 'RECOMMEND',
    ],
    [searchTypeKeyAtom, searchTypeKey],
    [globalFilterParamsAtom, filters],
    [checkInDateAtom, checkInDate],
    [checkOutDateAtom, checkOutDate],
    [capacityAdultAtom, capacityAdult],
    [capacityChildAtom, capacityChild],
    [capacityChildAgesAtom, capacityChildAges],
    [
      globalCheckIOCapacityAtom,
      {
        checkInDate: globalCheckInDate,
        checkOutDate: globalCheckOutDate,
        capacityRooms: globalCapacityRooms,
      },
    ],
    [
      quickFilterAtom,
      {
        globalFilter: searchHeader.globalFilter,
        quickFilter: searchHeader.quickFilter,
        hasSelectedFilter: !!searchHeader.hasSelectedFilter,
        includeFilterBtn: false,
      },
    ],
    [sortInfoAtom, searchHeader.sortInfo],
    [originalGlobalFilterAtom, searchHeader.globalFilter],
    [globalFilterAtom, searchHeader.globalFilter],
    [localLocationAtom, localLocation],
    [verticalTabInfoAtom, searchHeader.tabInfo?.items || []],
    [selectedVerticalTabInfoAtom, searchHeader.tabInfo?.items?.filter((item) => item.selected)[0]],
    [universalSearchRequestBodyAtom, searchHeader.request],
    [sessionKeyAtom, sessionKey],
    [hashedMemberNoAtom, hashedMemberNo],
    [isLocationAgreedAtom, isLocationAgreed],
  ]);

  return <>{children}</>;
}
